import { useMemoizedFn, useMount } from 'ahooks';
import { Divider, Layout, Tabs } from 'antd';
import { useAppDispatch } from 'app/hooks';
import { ADJUSTMENT_DATE_FORMAT, getFormatDate } from 'components/RevxDate';
import RevxLogoText from 'components/RevxLogoText';
import { RevxSpin } from 'components/RevxSpin';
import { SiteRoleEnum } from 'domains/roleDomain';
import {
  JobAdjustmentProcessStatusEnum,
  JobAdjustmentViewer,
  JobCancellationStatusEnum,
  JobCancellationViewer,
  JobItem,
  JobParticipantEnum,
  JobUIStatusEnum,
} from 'domains/workspaceDomain';
import { useRole, useUser } from 'hooks';
import { clone, find, includes, get } from 'lodash-es';
import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import workspaceService from 'services/jobService';
import { isBuyer, isSourcer, isSupplier } from 'utils/rbac';
import JobCancellationSource from '../components/JobCancellationSource';
import AdjustOriginal from './components/AdjustOriginal';
import BuyerAdjustOriginal from './components/BuyerAdjustOriginal';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { useTranslation } from 'react-i18next';
import { JOB_ADJUSTMENT_WORKSPACE_FILTER } from 'utils/constants';
import { sleep } from 'utils/misc';
import './index.less';

export enum TabsKeys {
  original = 'Original price',
  supplierAdjustment = 'Supplier adjustment',
  sourcerUpdate = 'Sourcer update',
  supplierCancelJob = 'Supplier cancel job',
  buyerCancelJob = 'Buyer cancel job',
  sourcerCancelJob = 'Sourcer cancel job',
  buyerViewJob = 'Adjusted price',
}

const JobAdjustment = () => {
  const dispatch = useAppDispatch();
  const params = useParams<{ jobId: string }>();
  const user = useUser();
  const [loading, setLoading] = useState<boolean>(false);
  const [jobData, setJobData] = useState<JobItem>();
  const [tabsActiveKey, setTabsActiveKey] = useState<TabsKeys>(
    TabsKeys.original
  );
  const [jobAdjustmentData, setJobAdjustmentData] =
    useState<JobAdjustmentViewer[]>();
  const [jobCancellations, setJobCancellations] =
    useState<JobCancellationViewer>();
  const [cancelBoxVisible, setCancelBoxVisible] = useState(false);

  const uiStatusFilter =
    localStorage.getItem(JOB_ADJUSTMENT_WORKSPACE_FILTER) ||
    JobUIStatusEnum.SAVE_QUOTES;
  const history = useHistory();
  const { Supplier, Sourcer } = SiteRoleEnum;
  const { AWAITING_APPROVAL, AWAITING_ACCEPT } = JobAdjustmentProcessStatusEnum;
  const { t } = useTranslation();
  const { supplierRole } = useRole();

  useMount(async () => {
    defaultJobAdjustment();
  });

  const goBackWorkspace = async () => {
    await sleep(1000);
    history.replace(`/workspace/${uiStatusFilter}`);
  };

  const defaultJobAdjustment = useMemoizedFn(async () => {
    try {
      setLoading(true);
      // const res = await getJobAdjustmentAllData();
      const jobItem = await workspaceService.getJobDetail(params.jobId);
      setJobData(jobItem);
      if (jobItem.cancellationStatus === JobCancellationStatusEnum.NONE) {
        const res = await dispatch.rfm.jobsAdjustmentsAll(params.jobId);
        setJobAdjustmentData(res);
        const lastTabKey = tabPane(res[res.length - 1], res.length - 1);
        setTabsActiveKey(lastTabKey || TabsKeys.original);
      } else {
        const resCancellations = await dispatch.rfm.getJobCancellations(
          params.jobId
        );
        setJobCancellations(resCancellations);
        setTabsActiveKey(TabsKeys.buyerCancelJob);
      }
    } finally {
      setLoading(false);
    }
  });

  const buyerAdjustShow = useMemoizedFn(
    (item: JobAdjustmentViewer, idx: number) => {
      return Boolean(
        idx !== 0 &&
          [
            JobAdjustmentProcessStatusEnum.AWAITING_ACCEPT,
            JobAdjustmentProcessStatusEnum.ACCEPTED,
          ].includes(item?.processStatus) &&
          (isBuyer(user) || isSupplier(user))
      );
    }
  );

  const updateData = (data: JobAdjustmentViewer) => {
    const { promoterRole: role } = data;
    if (!jobAdjustmentData) return;
    jobAdjustmentData.forEach(({ promoterRole }, index) => {
      if (role === promoterRole) {
        jobAdjustmentData.splice(index, 1, data);
      }
    });
    if (!jobAdjustmentData?.find(({ promoterRole }) => role === promoterRole)) {
      jobAdjustmentData.push(data);
    }
    setJobAdjustmentData(clone(jobAdjustmentData));
  };

  const tabPane = useMemoizedFn(
    (jobAdjustment: JobAdjustmentViewer, idx: number) => {
      if (buyerAdjustShow(jobAdjustment, idx)) {
        return TabsKeys.buyerViewJob;
      }
      if (!jobAdjustment?.promoterRole) {
        return TabsKeys.original;
      } else if (jobAdjustment.promoterRole === SiteRoleEnum.Supplier) {
        return TabsKeys.supplierAdjustment;
      } else if (jobAdjustment.promoterRole === SiteRoleEnum.Sourcer) {
        return TabsKeys.sourcerUpdate;
      }
    }
  );

  const jumpTabHandler = async (key: TabsKeys): Promise<void> => {
    const jobList = jobAdjustmentData?.map((item) =>
      item.promoterRole?.toLowerCase()
    );
    if (jobList && jobList.length) {
      jobList.forEach((job) => {
        if (job && key.toLowerCase().indexOf(job) > -1) {
          setTabsActiveKey(key);
          return Promise.resolve();
        }
      });
    }
    return Promise.reject();
  };

  const supplierCancelBtn = () => {
    const supplierJobItem = find(jobAdjustmentData, { promoterRole: Supplier });
    const sourcerJobItem = find(jobAdjustmentData, { promoterRole: Sourcer });
    const editingStatuses = [AWAITING_APPROVAL, AWAITING_ACCEPT];
    const isEditing = (item?: JobAdjustmentViewer) =>
      includes(editingStatuses, get(item, 'processStatus'));

    if (
      tabsActiveKey !== TabsKeys.original &&
      ((isSupplier(user) && isEditing(supplierJobItem)) ||
        (isSourcer(user) &&
          (isEditing(sourcerJobItem) || isEditing(supplierJobItem))))
    ) {
      return (
        <div>
          <RevxButton
            brandOrange={true}
            type="primary"
            onClick={() => {
              setCancelBoxVisible(true);
            }}
          >
            Cancel Job Adjustment
          </RevxButton>
        </div>
      );
    }
    return null;
  };

  const cancelJobHandler = async () => {
    setLoading(true);
    await workspaceService.cancelJobAdjustmentAll(jobData?.id || 0, 0);
    await goBackWorkspace();
    setLoading(false);
  };

  const { CUSTOMER, PRODUCER, PROXY } = JobParticipantEnum;
  const isOwnJob =
    jobData?.participants.includes(CUSTOMER) ||
    jobData?.participants.includes(PROXY);
  const isSupplierOwnJob = isOwnJob && jobData?.participants.includes(PRODUCER);
  const showCostCenterBySupplier =
    isSupplierOwnJob &&
    jobData?.participants.includes(PRODUCER) &&
    jobData?.participants.includes(PROXY);

  return (
    <Layout className="workspace-layout job-adjustment">
      <RevxSpin spinning={loading}>
        <div className="workspace-header">
          <RevxLogoText
            text="Job Adjustment"
            colors={[
              'gray',
              'gray',
              'gray',
              'blue',
              'red',
              'yellow',
              'black',
              'blue',
              'red',
              'gray',
              'gray',
              'gray',
              'gray',
            ]}
          />
        </div>
        {(!supplierRole || showCostCenterBySupplier) && (
          <RevxFlexLayout
            justify="justify-center"
            className="mt-20 main-job-info"
          >
            <div>
              <span className="job-info__label">Order Number:</span>
              <span>{jobData?.orderId}</span>
            </div>
            <Divider type="vertical" />
            <div>
              <span className="job-info__label">Job Number:</span>
              <span>{params.jobId}</span>
            </div>
          </RevxFlexLayout>
        )}

        {cancelBoxVisible && (
          <RevxFlexLayout className="cancel-box" justify="justify-center">
            <span className="bold">
              {t('workspace.jobAdjust.cancelJobTips')}
            </span>
            <RevxButton
              type="primary"
              brandGreen={true}
              className="ml-20"
              onClick={async () => {
                await cancelJobHandler();
              }}
            >
              Yes, cancel
            </RevxButton>
            <RevxButton
              type="default"
              brandGreen={true}
              className="ml-10"
              onClick={() => {
                setCancelBoxVisible(false);
              }}
            >
              No, don't cancel
            </RevxButton>
          </RevxFlexLayout>
        )}
        <div className="workspace-content adjustment-main">
          <div className="adjustment-main_tabs">
            {jobAdjustmentData?.length === 1 && !isBuyer(user) ? (
              <div className="workspace-section">
                <div className="workspace-item">
                  <AdjustOriginal
                    data={jobAdjustmentData[0]}
                    jobId={+params.jobId}
                    goBackWorkspace={goBackWorkspace}
                    updateJobAdjustmentData={updateData}
                    jumpTab={jumpTabHandler}
                    refreshJobAdjustmentData={defaultJobAdjustment}
                    jobData={jobData}
                  />
                </div>
              </div>
            ) : (
              <Tabs
                activeKey={tabsActiveKey}
                tabBarExtraContent={{ right: supplierCancelBtn() }}
                onChange={(v: string) => {
                  setTabsActiveKey(v as TabsKeys);
                }}
              >
                {jobAdjustmentData?.map((item, idx) => {
                  return (
                    <Tabs.TabPane
                      tab={`${tabPane(item, idx)} ${
                        item.promoterRole
                          ? getFormatDate({
                              dateStr: item.updateAt || '',
                              formatStr: ADJUSTMENT_DATE_FORMAT,
                              isLocalTime: true,
                            })
                          : ''
                      }`}
                      key={tabPane(item, idx)}
                    >
                      <div className="workspace-section">
                        <div className="workspace-item">
                          {buyerAdjustShow(item, idx) ? (
                            <BuyerAdjustOriginal
                              data={item}
                              updateJobAdjustmentData={updateData}
                              setLoading={setLoading}
                              originalData={jobAdjustmentData[0]}
                              jobData={jobData}
                            />
                          ) : (
                            <AdjustOriginal
                              originalData={jobAdjustmentData[0]}
                              data={item}
                              jobId={+params.jobId}
                              goBackWorkspace={goBackWorkspace}
                              updateJobAdjustmentData={updateData}
                              jumpTab={jumpTabHandler}
                              refreshJobAdjustmentData={defaultJobAdjustment}
                              jobData={jobData}
                            />
                          )}
                        </div>
                      </div>
                    </Tabs.TabPane>
                  );
                })}
                {jobCancellations && (
                  <Tabs.TabPane
                    tab={`${
                      jobCancellations.promoterRole === SiteRoleEnum.Sourcer
                        ? TabsKeys.sourcerCancelJob
                        : jobCancellations.promoterRole ===
                          SiteRoleEnum.Supplier
                        ? TabsKeys.supplierCancelJob
                        : TabsKeys.buyerCancelJob
                    } ${getFormatDate({
                      dateStr: jobCancellations.updateAt || '',
                      formatStr: ADJUSTMENT_DATE_FORMAT,
                      isLocalTime: true,
                    })}`}
                    key={TabsKeys.buyerCancelJob}
                  >
                    <div className="workspace-section">
                      <div className="workspace-item">
                        <JobCancellationSource
                          data={jobCancellations}
                          setJobCancellations={setJobCancellations}
                          jobData={jobData}
                        />
                      </div>
                    </div>
                  </Tabs.TabPane>
                )}
              </Tabs>
            )}
          </div>
        </div>
      </RevxSpin>
    </Layout>
  );
};

export default JobAdjustment;
